// Dedicated UI sections for Doctor Dashboard that match baby app exactly
import React, { useState, useEffect } from 'react';
import { Heart, Calendar, Syringe, CheckCircle, AlertCircle, Clock, Plus, TrendingUp, FileText, Stethoscope, ShieldAlert, Baby, Thermometer, Ruler, Weight, Printer, Eye, EyeOff, ChevronDown, ChevronUp, Edit2, Trash2, User, Activity } from 'lucide-react';
import ChartOverlay from './ChartOverlay';
import WHOGrowthCharts from './WHOGrowthCharts';
import { getChartPoints } from '../utils/growthChartUtils';
import { calculateBMI, getBMIBadgeClasses } from '../utils/bmiCalculator';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';

// Helper function to clean medication text for display in session cards and popups
const cleanMedicationForDisplay = (medicationText) => {
  if (!medicationText) return '';
  
  return medicationText
    .split(' | ')
    .map(med => {
      let cleanMed = med.trim();
      
      // Remove [Type: xxx] pattern
      cleanMed = cleanMed.replace(/\s*\[Type:\s*[^\]]+\]/gi, '');
      
      // Remove [Generic: xxx] pattern
      cleanMed = cleanMed.replace(/\s*\[Generic:\s*[^\]]+\]/gi, '');
      
      // Remove [For: xxx] pattern
      cleanMed = cleanMed.replace(/\s*\[For:\s*[^\]]+\]/gi, '');
      
      // Clean up extra spaces
      cleanMed = cleanMed.replace(/\s+/g, ' ').trim();
      
      return cleanMed;
    })
    .join(' | ');
};

// Vitals Status Helper Function
const getVitalStatus = (type, value) => {
  if (!value || value === '') return null;
  
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return null;
  
  switch(type) {
    case 'temp':
      if (numValue < 97) return { status: 'Low', color: 'bg-blue-100 text-blue-800' };
      if (numValue >= 97 && numValue <= 99) return { status: 'Normal', color: 'bg-green-100 text-green-800' };
      if (numValue > 99) return { status: 'High', color: 'bg-red-100 text-red-800' };
      break;
    case 'bp_systolic':
      if (numValue < 90) return { status: 'Low', color: 'bg-blue-100 text-blue-800' };
      if (numValue >= 90 && numValue <= 120) return { status: 'Normal', color: 'bg-green-100 text-green-800' };
      if (numValue > 120) return { status: 'High', color: 'bg-red-100 text-red-800' };
      break;
    case 'bp_diastolic':
      if (numValue < 60) return { status: 'Low', color: 'bg-blue-100 text-blue-800' };
      if (numValue >= 60 && numValue <= 80) return { status: 'Normal', color: 'bg-green-100 text-green-800' };
      if (numValue > 80) return { status: 'High', color: 'bg-red-100 text-red-800' };
      break;
    case 'pulse':
      if (numValue < 60) return { status: 'Low', color: 'bg-blue-100 text-blue-800' };
      if (numValue >= 60 && numValue <= 100) return { status: 'Normal', color: 'bg-green-100 text-green-800' };
      if (numValue > 100) return { status: 'High', color: 'bg-red-100 text-red-800' };
      break;
    case 'spo2':
      if (numValue < 95) return { status: 'Low', color: 'bg-orange-100 text-orange-800' };
      if (numValue >= 95) return { status: 'Normal', color: 'bg-green-100 text-green-800' };
      break;
    default:
      return null;
  }
  return null;
};

// Medical Conditions Section UI (matching MedicalConditions.js)
export const MedicalConditionsSection = ({ 
  conditions, 
  temperatureReadings = [],
  bloodPressureReadings = [],
  onAdd, 
  onEdit,
  onDelete,
  onAddTemp,
  onEditTemp,
  onDeleteTemp,
  onAddBp,
  onEditBp,
  onDeleteBp,
  currentUserId,
  formatDate, 
  viewOnlyMode = true 
}) => {
  const [activeTab, setActiveTab] = useState('temperature');
  const [activeBpTab, setActiveBpTab] = useState('blood_pressure');

  const getStatusBadge = (status) => {
    if (status === 'Active') {
      return <Badge className="bg-red-100 text-red-700">Active</Badge>;
    } else if (status === 'Resolved') {
      return <Badge className="bg-green-100 text-green-700">Resolved</Badge>;
    } else if (status === 'Monitoring') {
      return <Badge className="bg-yellow-100 text-yellow-700">Monitoring</Badge>;
    }
    return null;
  };

  return (
    <>
      {/* Medical Conditions List */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
            Medical Conditions
          </h3>
          {!viewOnlyMode && (
            <Button 
              onClick={onAdd}
              className="bg-gray-900 hover:bg-gray-800 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Condition
            </Button>
          )}
        </div>
        
        <div className="p-6">
          {conditions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Heart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p>No medical conditions recorded</p>
            </div>
          ) : (
            <div className="space-y-4">
              {conditions.map((condition) => (
                <div key={condition.id} className="flex items-start justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                        {condition.condition_name}
                      </h4>
                      {getStatusBadge(condition.status)}
                    </div>
                    {condition.description && (
                      <p className="text-gray-600 text-sm leading-relaxed">{condition.description}</p>
                    )}
                    {condition.medication && (
                      <div className="text-sm text-gray-700 mt-2 bg-blue-50 p-2 rounded">
                        <span className="font-medium">Medication:</span> {condition.medication}
                      </div>
                    )}
                    {condition.diagnosed_date && (
                      <div className="text-sm text-gray-500 flex items-center space-x-1 mt-2">
                        <Calendar className="w-4 h-4" />
                        <span>Diagnosed: {formatDate(condition.diagnosed_date)}</span>
                      </div>
                    )}
                    {condition.updated_by_doctor && (
                      <p className="text-xs text-purple-600 mt-2">
                        Updated by {condition.updated_by_doctor}
                        {condition.updated_by_hospital && ` - ${condition.updated_by_hospital}`}
                      </p>
                    )}
                  </div>
                  
                  {/* Edit/Delete buttons - only show for conditions created by current doctor */}
                  {!viewOnlyMode && condition.updated_by === currentUserId && (
                    <div className="flex items-center space-x-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(condition)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDelete(condition.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Body Temperature Monitoring Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-orange-100 overflow-hidden">
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Thermometer className="w-6 h-6 text-orange-600" />
              <h3 className="text-lg font-semibold text-orange-900" style={{fontFamily: 'Space Grotesk'}}>
                Body Temperature
              </h3>
            </div>
            {!viewOnlyMode && onAddTemp && (
              <Button
                onClick={onAddTemp}
                size="sm"
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Reading
              </Button>
            )}
          </div>
        </div>
        
        {/* Temperature Tabs */}
        <div className="border-b">
          <div className="flex">
            <button 
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'temperature' 
                  ? 'border-orange-500 text-orange-600 bg-orange-50' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('temperature')}
            >
              Temperature
            </button>
            <button 
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'history' 
                  ? 'border-orange-500 text-orange-600 bg-orange-50' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('history')}
            >
              History
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {activeTab === 'temperature' && (
            <div>
              {temperatureReadings.length > 0 ? (
                <div className="border-2 border-orange-200 bg-orange-50 rounded-lg p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="text-3xl font-bold text-orange-600">
                          {temperatureReadings[0].temperature}°{temperatureReadings[0].unit}
                        </div>
                        <span className="text-xs bg-orange-500 text-white px-3 py-1 rounded-full">LATEST</span>
                        {temperatureReadings[0].category && (
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                            temperatureReadings[0].color === 'green' ? 'bg-green-100 text-green-800' :
                            temperatureReadings[0].color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {temperatureReadings[0].category}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-700 space-y-1">
                        <div className="font-medium">Method: {temperatureReadings[0].method}</div>
                        <div>Date: {formatDate(temperatureReadings[0].date_recorded)} at {temperatureReadings[0].time_recorded}</div>
                        {temperatureReadings[0].notes && <div>Notes: {temperatureReadings[0].notes}</div>}
                      </div>
                      {temperatureReadings[0].updated_by_doctor && (
                        <p className="text-xs text-purple-600 mt-2">
                          Updated by {temperatureReadings[0].updated_by_doctor}
                          {temperatureReadings[0].updated_by_hospital && ` - ${temperatureReadings[0].updated_by_hospital}`}
                        </p>
                      )}
                    </div>
                    <Thermometer className="w-8 h-8 text-orange-400" />
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Thermometer className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p>No temperature readings recorded</p>
                  <p className="text-sm">Temperature readings will appear here</p>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'history' && (
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Temperature History</h3>
                <p className="text-sm text-gray-500">All recorded temperature readings</p>
              </div>
              
              {temperatureReadings.length > 0 ? (
                <div className="space-y-3">
                  {temperatureReadings.map((reading, index) => (
                    <div key={reading.id} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-1">
                            <div className="font-semibold text-gray-900">
                              {reading.temperature}°{reading.unit}
                            </div>
                            {index === 0 && (
                              <span className="text-xs bg-orange-500 text-white px-2 py-1 rounded-full font-medium">LATEST</span>
                            )}
                            {reading.category && (
                              <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                reading.color === 'green' ? 'bg-green-100 text-green-800' :
                                reading.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {reading.category}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600">
                            {formatDate(reading.date_recorded)} at {reading.time_recorded} • {reading.method}
                          </div>
                          {reading.notes && (
                            <div className="text-xs text-gray-500 mt-1">{reading.notes}</div>
                          )}
                          {reading.updated_by_doctor && (
                            <p className="text-xs text-purple-600 mt-1">
                              Updated by {reading.updated_by_doctor}
                              {reading.updated_by_hospital && ` - ${reading.updated_by_hospital}`}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Thermometer className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p>No temperature readings recorded</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Blood Pressure Card - Separate Section */}
      <div className="mt-6 bg-white rounded-2xl shadow-sm border border-red-100 overflow-hidden">
        <div className="bg-gradient-to-r from-red-50 to-red-100 px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Heart className="w-6 h-6 text-red-600" />
              <h3 className="text-lg font-semibold text-red-900" style={{fontFamily: 'Space Grotesk'}}>
                Blood Pressure Monitoring
              </h3>
            </div>
            {!viewOnlyMode && onAddBp && (
              <Button
                onClick={onAddBp}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Reading
              </Button>
            )}
          </div>
        </div>

        <div>
          {/* Blood Pressure Tabs */}
          <div className="border-b">
            <div className="flex">
              <button 
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeBpTab === 'blood_pressure' 
                    ? 'border-red-500 text-red-600 bg-red-50' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveBpTab('blood_pressure')}
              >
                Blood Pressure
              </button>
              <button 
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeBpTab === 'bp_history' 
                    ? 'border-red-500 text-red-600 bg-red-50' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveBpTab('bp_history')}
              >
                History
              </button>
            </div>
          </div>

          {activeBpTab === 'blood_pressure' && (
            <div className="p-6">
              {bloodPressureReadings.length > 0 ? (
                <div>
                  {/* Show only the latest reading - matching temperature card layout */}
                  <div className="border-2 border-red-200 bg-red-50 rounded-lg p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="text-3xl font-bold text-red-600">
                            {bloodPressureReadings[0].systolic}/{bloodPressureReadings[0].diastolic} mmHg
                          </div>
                          <span className="text-xs bg-red-500 text-white px-3 py-1 rounded-full">LATEST</span>
                          {bloodPressureReadings[0].category && (
                            <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                              bloodPressureReadings[0].color === 'blue' ? 'bg-blue-100 text-blue-800' :
                              bloodPressureReadings[0].color === 'green' ? 'bg-green-100 text-green-800' :
                              bloodPressureReadings[0].color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {bloodPressureReadings[0].category}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-700 space-y-1">
                          <div className="font-medium">Method: {bloodPressureReadings[0].method}</div>
                          {bloodPressureReadings[0].pulse && <div>Pulse: {bloodPressureReadings[0].pulse} bpm</div>}
                          <div>Date: {formatDate(bloodPressureReadings[0].date_recorded)} at {bloodPressureReadings[0].time_recorded}</div>
                          {bloodPressureReadings[0].notes && <div>Notes: {bloodPressureReadings[0].notes}</div>}
                        </div>
                        {bloodPressureReadings[0].updated_by_doctor && (
                          <p className="text-xs text-purple-600 mt-2">
                            Updated by {bloodPressureReadings[0].updated_by_doctor}
                            {bloodPressureReadings[0].updated_by_hospital && ` - ${bloodPressureReadings[0].updated_by_hospital}`}
                          </p>
                        )}
                      </div>
                      <Heart className="w-8 h-8 text-red-400" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Heart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p>No blood pressure readings recorded</p>
                  <p className="text-sm">Blood pressure readings will appear here</p>
                </div>
              )}
            </div>
          )}

          {activeBpTab === 'bp_history' && (
            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Blood Pressure History</h3>
                <p className="text-sm text-gray-500">All recorded blood pressure readings</p>
              </div>
              
              {bloodPressureReadings.length > 0 ? (
                <div className="space-y-3">
                  {bloodPressureReadings.map((reading, index) => (
                    <div key={reading.id} className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-1">
                            <div className="font-semibold text-gray-900">
                              {reading.systolic}/{reading.diastolic} mmHg
                            </div>
                            {reading.pulse && (
                              <div className="text-sm text-gray-600">
                                Pulse: {reading.pulse} bpm
                              </div>
                            )}
                            {index === 0 && (
                              <span className="text-xs bg-red-500 text-white px-2 py-1 rounded-full font-medium">LATEST</span>
                            )}
                            {reading.category && (
                              <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                reading.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                                reading.color === 'green' ? 'bg-green-100 text-green-800' :
                                reading.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {reading.category}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600">
                            {formatDate(reading.date_recorded)} at {reading.time_recorded} • {reading.method}
                          </div>
                          {reading.notes && (
                            <div className="text-xs text-gray-500 mt-1">{reading.notes}</div>
                          )}
                          {reading.updated_by_doctor && (
                            <p className="text-xs text-purple-600 mt-1">
                              Updated by {reading.updated_by_doctor}
                              {reading.updated_by_hospital && ` - ${reading.updated_by_hospital}`}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Heart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p>No blood pressure readings recorded</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Allergies Section UI (matching Allergies.js)
export const AllergiesSection = ({ 
  allergies, 
  onAdd,
  onEdit,
  onDelete,
  formatDate, 
  viewOnlyMode = true,
  currentUserId = null
}) => {
  const getSeverityBadge = (severity) => {
    const colors = {
      'Mild': 'bg-yellow-100 text-yellow-700',
      'Moderate': 'bg-orange-100 text-orange-700',
      'Severe': 'bg-red-100 text-red-700'
    };
    return <Badge className={colors[severity] || colors['Mild']}>{severity}</Badge>;
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
          Allergies
        </h3>
        {!viewOnlyMode && (
          <Button 
            onClick={onAdd}
            className="bg-gray-900 hover:bg-gray-800 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Allergy
          </Button>
        )}
      </div>
      
      <div className="p-6">
        {allergies.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ShieldAlert className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p>No allergies recorded</p>
          </div>
        ) : (
          <div className="space-y-4">
            {allergies.map((allergy) => (
              <div key={allergy.id} className="p-4 border border-orange-200 rounded-lg bg-orange-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                        {allergy.allergen}
                      </h4>
                      {getSeverityBadge(allergy.severity)}
                    </div>
                    {allergy.reaction && (
                      <p className="text-gray-600 text-sm mb-2">
                        <strong>Reaction:</strong> {allergy.reaction}
                      </p>
                    )}
                    {allergy.discovered_date && (
                      <div className="text-sm text-gray-500 flex items-center space-x-1 mb-2">
                        <Calendar className="w-4 h-4" />
                        <span>Discovered: {formatDate(allergy.discovered_date)}</span>
                      </div>
                    )}
                    {allergy.updated_by_doctor && (
                      <p className="text-xs text-purple-600 mt-2">
                        Updated by {allergy.updated_by_doctor}
                        {allergy.updated_by_hospital && ` - ${allergy.updated_by_hospital}`}
                      </p>
                    )}
                  </div>
                  
                  {/* Edit/Delete buttons - only show for allergies created by current doctor */}
                  {!viewOnlyMode && onEdit && onDelete && (!allergy.updated_by || allergy.updated_by === currentUserId) && (
                    <div className="flex items-center space-x-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(allergy)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDelete(allergy.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Vaccinations Section UI (matching Vaccinations.js)
export const VaccinationsSection = ({ 
  vaccinations, 
  onMarkAsGiven, 
  onUpdateSchedule,
  onAddVaccination,
  formatDate, 
  viewOnlyMode = true 
}) => {
  // State for collapse/expand
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [showSchedule, setShowSchedule] = useState(true);

  const toggleGroupCollapse = (ageGroup) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [ageGroup]: !prev[ageGroup]
    }));
  };

  const toggleAllGroups = () => {
    const allGroups = Object.keys(getVaccinationsByAgeGroup());
    const allCollapsed = allGroups.every(group => collapsedGroups[group]);
    
    const newState = {};
    allGroups.forEach(group => {
      newState[group] = !allCollapsed;
    });
    setCollapsedGroups(newState);
  };

  const areAllGroupsCollapsed = () => {
    const allGroups = Object.keys(getVaccinationsByAgeGroup());
    if (allGroups.length === 0) return false;
    return allGroups.every(group => collapsedGroups[group]);
  };

  const getStatusBadge = (vaccination) => {
    const dueDate = new Date(vaccination.due_date);
    const today = new Date();
    
    if (vaccination.status === 'given') {
      return <Badge className="bg-green-100 text-green-700">Given</Badge>;
    } else if (today > dueDate) {
      return <Badge className="bg-red-100 text-red-700">Overdue</Badge>;
    } else {
      return <Badge className="bg-yellow-100 text-yellow-700">Due</Badge>;
    }
  };

  const getStatusIcon = (vaccination) => {
    const dueDate = new Date(vaccination.due_date);
    const today = new Date();
    
    if (vaccination.status === 'given') {
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    } else if (today > dueDate) {
      return <AlertCircle className="w-5 h-5 text-red-600" />;
    } else {
      return <Clock className="w-5 h-5 text-yellow-600" />;
    }
  };

  const getVaccinationProgress = () => {
    // Only count scheduled vaccinations (those with age_group) for progress
    const scheduledVaccinations = vaccinations.filter(v => v.age_group);
    const total = scheduledVaccinations.length;
    const given = scheduledVaccinations.filter(v => v.status === 'given').length;
    const percentage = total > 0 ? Math.round((given / total) * 100) : 0;
    return { total, given, percentage };
  };

  const getVaccinationsByAgeGroup = () => {
    const grouped = {};
    // Only group vaccinations that have age_group (scheduled vaccinations)
    vaccinations.forEach(v => {
      if (v.age_group) {
        const age = v.age_group;
        if (!grouped[age]) grouped[age] = [];
        grouped[age].push(v);
      }
    });
    return grouped;
  };

  // Get manually added vaccinations (those without age_group)
  const getManuallyAddedVaccinations = () => {
    return vaccinations.filter(v => !v.age_group || v.age_group === null);
  };

  const progress = getVaccinationProgress();

  return (
    <div className="space-y-6">
      {/* Action Buttons - Top Right */}
      {!viewOnlyMode && (
        <div className="flex gap-2 justify-end">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onAddVaccination}
            className="text-sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Vaccination
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onUpdateSchedule}
            className="text-sm"
          >
            Update Schedule
          </Button>
        </div>
      )}

      {/* Progress Overview */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>Vaccination Progress</h3>
            <p className="text-gray-600">{progress.given} of {progress.total} vaccinations completed</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600">{progress.percentage}%</div>
            <div className="text-sm text-gray-500">Complete</div>
          </div>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500"
            style={{ width: `${progress.percentage}%` }}
          ></div>
        </div>
      </div>

      {/* Control Buttons - Below Progress */}
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowSchedule(!showSchedule)}
          className="text-sm"
        >
          {showSchedule ? (
            <>
              <EyeOff className="w-4 h-4 mr-1" />
              Hide Schedule
            </>
          ) : (
            <>
              <Eye className="w-4 h-4 mr-1" />
              Show Schedule
            </>
          )}
        </Button>
        
        {showSchedule && Object.keys(getVaccinationsByAgeGroup()).length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={toggleAllGroups}
            className="text-sm"
          >
            {areAllGroupsCollapsed() ? (
              <>
                <ChevronDown className="w-4 h-4 mr-1" />
                Expand All
              </>
            ) : (
              <>
                <ChevronUp className="w-4 h-4 mr-1" />
                Collapse All
              </>
            )}
          </Button>
        )}
      </div>

      {/* Vaccinations List by Age Group */}
      {showSchedule && vaccinations.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
          <Syringe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Vaccination Schedule</h3>
          <p className="text-gray-600">No vaccination records found.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Scheduled Vaccinations */}
          {showSchedule && Object.entries(getVaccinationsByAgeGroup()).map(([ageGroup, groupVaccinations]) => (
            <div key={ageGroup} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-4 border-b border-gray-100 cursor-pointer hover:from-blue-100 hover:to-purple-100 transition-colors"
                onClick={() => toggleGroupCollapse(ageGroup)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                      {ageGroup}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {groupVaccinations.filter(v => v.status === 'given').length} of {groupVaccinations.length} vaccines completed
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-4"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroupCollapse(ageGroup);
                    }}
                  >
                    {collapsedGroups[ageGroup] ? (
                      <ChevronDown className="w-5 h-5" />
                    ) : (
                      <ChevronUp className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </div>
              
              {!collapsedGroups[ageGroup] && (
                <div className="p-6 space-y-4">
                  {groupVaccinations.map((vaccine) => (
                  <div 
                    key={vaccine.id} 
                    className={`p-4 rounded-lg border ${
                      vaccine.status === 'given' ? 'bg-green-50 border-green-200' : 
                      new Date(vaccine.due_date) < new Date() ? 'bg-red-50 border-red-200' : 
                      'bg-yellow-50 border-yellow-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          {getStatusIcon(vaccine)}
                          <h4 className="font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                            {vaccine.vaccine_name}
                          </h4>
                          {getStatusBadge(vaccine)}
                        </div>
                        
                        {vaccine.due_date && (
                          <div className="text-sm text-gray-600 flex items-center space-x-1 mb-1">
                            <Calendar className="w-4 h-4" />
                            <span>Due: {formatDate(vaccine.due_date)}</span>
                          </div>
                        )}
                        
                        {vaccine.given_date && (
                          <>
                            <div className="text-sm text-green-700 font-medium mt-1">
                              Given on: {formatDate(vaccine.given_date)}
                            </div>
                            {vaccine.batch_number && (
                              <div className="text-xs text-gray-500 mt-1">Batch: {vaccine.batch_number}</div>
                            )}
                            {vaccine.trade_name && (
                              <div className="text-xs text-gray-500">Trade Name: {vaccine.trade_name}</div>
                            )}
                          </>
                        )}
                      </div>
                      
                      {!viewOnlyMode && vaccine.status !== 'given' && (
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700 text-white ml-4"
                          onClick={() => onMarkAsGiven(vaccine)}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Mark as Given
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          ))}

          {/* Manually Added Vaccinations Section */}
          {getManuallyAddedVaccinations().length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                  Additional Vaccinations
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Manually added vaccinations ({getManuallyAddedVaccinations().length})
                </p>
              </div>
              
              <div className="divide-y divide-gray-100">
                {getManuallyAddedVaccinations().map((vaccination) => (
                  <div key={vaccination.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="flex-shrink-0 mt-1">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                              {vaccination.vaccine_name}
                            </h4>
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Given</Badge>
                          </div>
                          
                          <div className="space-y-1 text-sm text-gray-600">
                            {vaccination.given_date && (
                              <div className="flex items-center space-x-2">
                                <Calendar className="w-4 h-4" />
                                <span className="text-green-600">Given: {formatDate(vaccination.given_date)}</span>
                              </div>
                            )}
                            {vaccination.trade_name && (
                              <div>Trade Name: {vaccination.trade_name}</div>
                            )}
                            {vaccination.batch_number && (
                              <div>Batch: {vaccination.batch_number}</div>
                            )}
                            {vaccination.notes && (
                              <div className="text-gray-500 italic">Notes: {vaccination.notes}</div>
                            )}
                            {vaccination.updated_by_doctor && (
                              <p className="text-xs text-purple-600 mt-2">
                                Updated by {vaccination.updated_by_doctor}
                                {vaccination.updated_by_hospital && ` - ${vaccination.updated_by_hospital}`}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Growth Tracker Section UI (matching GrowthTracker.js)
export const GrowthTrackerSection = ({ 
  growthRecords, 
  baby,
  onAdd, 
  onDelete,
  formatDate, 
  viewOnlyMode = true,
  currentDoctorId = null
}) => {
  const [chartPoints, setChartPoints] = useState(null);

  // Helper function to generate SVG overlay markup for popup windows
  // Now shows only the LATEST data point
  const generateOverlaySVG = (points, width, height, color, rotate) => {
    if (!points || points.length === 0) return '';
    
    // Get only the latest point
    const latestPoint = points[points.length - 1];
    
    return `
      <div style="position: absolute; inset: 0; pointer-events: none; display: flex; align-items: center; justify-content: center; z-index: 10;">
        <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" 
             style="transform: ${rotate ? 'rotate(90deg)' : 'none'};">
          <circle cx="${latestPoint.x}" cy="${latestPoint.y}" r="6" 
                  fill="${color}" stroke="white" 
                  stroke-width="2" opacity="0.9" />
          <circle cx="${latestPoint.x}" cy="${latestPoint.y}" r="10" fill="none" 
                  stroke="${color}" stroke-width="2" opacity="0.5" />
          <text x="${latestPoint.x}" y="${latestPoint.y - 15}" text-anchor="middle" 
                font-size="10" font-weight="bold" fill="${color}" 
                style="text-shadow: 0 0 3px white, 0 0 3px white">LATEST</text>
        </svg>
      </div>
    `;
  };

  // Calculate chart points when growth records change
  useEffect(() => {
    console.log('Doctor Dashboard - Chart points useEffect:', {
      hasRecords: growthRecords.length > 0,
      recordCount: growthRecords.length,
      hasBirthDate: !!baby?.date_of_birth,
      birthDate: baby?.date_of_birth
    });
    
    if (growthRecords.length > 0 && baby?.date_of_birth) {
      console.log('Doctor Dashboard - Calculating chart points:', {
        birthDate: baby.date_of_birth,
        records: growthRecords
      });
      const points = getChartPoints(growthRecords, baby.date_of_birth);
      console.log('Doctor Dashboard - Chart points calculated:', points);
      setChartPoints(points);
    }
  }, [growthRecords, baby]);

  // Calculate growth trends
  const getGrowthTrend = (current, previous) => {
    if (!previous) return null;
    const diff = (parseFloat(current) - parseFloat(previous)).toFixed(2);
    return { change: diff };
  };

  const getLatestMeasurements = () => {
    if (growthRecords.length === 0) return null;
    const latest = growthRecords[0];
    const previous = growthRecords[1];
    
    return {
      current: latest,
      heightTrend: previous ? getGrowthTrend(latest.height, previous.height) : null,
      weightTrend: previous ? getGrowthTrend(latest.weight, previous.weight) : null
    };
  };

  const measurements = getLatestMeasurements();

  return (
    <>
      {/* 1. Latest Measurements Overview */}
      {measurements && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{fontFamily: 'Space Grotesk'}}>Latest Measurements</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Ruler className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{measurements.current.height} cm</div>
                <div className="text-sm text-gray-600">Height</div>
                {measurements.heightTrend && (
                  <div className={`text-xs ${parseFloat(measurements.heightTrend.change) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {parseFloat(measurements.heightTrend.change) >= 0 ? '+' : ''}{measurements.heightTrend.change} cm
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Weight className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{measurements.current.weight} kg</div>
                <div className="text-sm text-gray-600">Weight</div>
                {measurements.weightTrend && (
                  <div className={`text-xs ${parseFloat(measurements.weightTrend.change) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {parseFloat(measurements.weightTrend.change) >= 0 ? '+' : ''}{measurements.weightTrend.change} kg
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2. Growth Records List */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
            Growth History
          </h3>
          {!viewOnlyMode && (
            <Button 
              onClick={onAdd}
              className="bg-gray-900 hover:bg-gray-800 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Record
            </Button>
          )}
        </div>
        
        {growthRecords.length === 0 ? (
          <div className="p-8 text-center">
            <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Growth Records</h3>
            <p className="text-gray-600 mb-4">Start tracking growth by adding the first measurement.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 overflow-y-auto max-h-[320px]">
            {growthRecords.map((record, index) => (
              <div key={record.id} className="px-6 py-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${index === 0 ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <Calendar className={`w-5 h-5 ${index === 0 ? 'text-green-600' : 'text-gray-600'}`} />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-semibold text-gray-900 text-base">{formatDate(record.date || record.date_recorded)}</h4>
                        {index === 0 && (
                          <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full font-medium">LATEST</span>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700">
                        <span><strong>H:</strong> {record.height} cm</span>
                        <span><strong>W:</strong> {record.weight} kg</span>
                        {record.head_circumference && (
                          <span><strong>HC:</strong> {record.head_circumference} cm</span>
                        )}
                      </div>
                      
                      {record.notes && (
                        <p className="mt-1 text-sm text-gray-500 truncate max-w-xs">{record.notes}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Delete button - only show if doctor created this record */}
                  {!viewOnlyMode && onDelete && currentDoctorId && record.created_by === currentDoctorId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(record.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                      title="Delete this growth record"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 3. WHO Growth Charts Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{fontFamily: 'Space Grotesk'}}>WHO Growth Charts</h3>
        <WHOGrowthCharts 
          key={`who-charts-${growthRecords?.length || 0}-${growthRecords?.[0]?.id || 'none'}`}
          growthRecords={growthRecords} 
          baby={baby}
          sex={baby?.sex || 'male'}
        />
      </div>
    </>
  );
};

// Birth Record Section UI (matching BirthRecord.js)
export const BirthRecordSection = ({ 
  baby, 
  onUpdate, 
  viewOnlyMode = true 
}) => {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
          Birth Details
        </h3>
        {!viewOnlyMode && (
          <Button 
            onClick={onUpdate}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            Update Birth Record
          </Button>
        )}
      </div>
      
      <div className="p-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-purple-50 rounded-lg">
            <p className="text-sm text-gray-500 mb-1">Mode of Delivery</p>
            <p className="font-semibold text-gray-900">{baby.mode_of_delivery || 'N/A'}</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <p className="text-sm text-gray-500 mb-1">Birth Weight</p>
            <p className="font-semibold text-gray-900">
              {baby.birth_weight ? `${baby.birth_weight} kg` : 'N/A'}
            </p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <p className="text-sm text-gray-500 mb-1">Birth Length</p>
            <p className="font-semibold text-gray-900">
              {(baby.birth_length || baby.length) ? `${baby.birth_length || baby.length} cm` : 'N/A'}
            </p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <p className="text-sm text-gray-500 mb-1">Head Circumference</p>
            <p className="font-semibold text-gray-900">
              {(baby.birth_head_circumference || baby.head_circumference) ? `${baby.birth_head_circumference || baby.head_circumference} cm` : 'N/A'}
            </p>
          </div>
          {baby.hospital_name && (
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">Hospital Name</p>
              <p className="font-semibold text-gray-900">{baby.hospital_name}</p>
            </div>
          )}
          {baby.doctor_name && (
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">Doctor Name</p>
              <p className="font-semibold text-gray-900">{baby.doctor_name}</p>
            </div>
          )}
        </div>
        
        {/* Antenatal History */}
        {baby.antenatal_history && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-500 mb-1">Antenatal History</p>
            <p className="text-gray-900 whitespace-pre-wrap">{baby.antenatal_history}</p>
          </div>
        )}
        
        {/* Birth History */}
        {baby.birth_history && (
          <div className="mt-4 p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-gray-500 mb-1">Birth History</p>
            <p className="text-gray-900 whitespace-pre-wrap">{baby.birth_history}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Medical Sessions Section UI (matching MedicalSessions.js - Guest View)
export const MedicalSessionsSection = ({ 
  sessions, 
  baby,
  onAdd, 
  onFollowUp,
  onEdit,
  onDelete,
  onViewPrevious,
  onViewSOAP,
  formatDate, 
  viewOnlyMode = true,
  currentUserId = null,
  API 
}) => {
  const [selectedMonth, setSelectedMonth] = React.useState('all');
  const [selectedYear, setSelectedYear] = React.useState('all');
  const [doctorSearchQuery, setDoctorSearchQuery] = React.useState('');
  const [filteredSessions, setFilteredSessions] = React.useState([]);
  const [showPrintPreview, setShowPrintPreview] = React.useState(false);
  const [printSession, setPrintSession] = React.useState(null);
  const [showViewDialog, setShowViewDialog] = React.useState(false);
  const [viewSession, setViewSession] = React.useState(null);
  const [sessionReports, setSessionReports] = React.useState({});
  const [reanalyzingReports, setReanalyzingReports] = React.useState({});
  const [medicalBackground, setMedicalBackground] = React.useState({
    conditions: [],
    medications: [],
    allergies: []
  });

  // Fetch reports for all sessions
  React.useEffect(() => {
    const fetchReportsForSessions = async () => {
      const reportsMap = {};
      
      for (const session of sessions) {
        try {
          const response = await axios.get(`${API}/api/report-uploads/session/${session.id}`);
          if (response.data && response.data.length > 0) {
            reportsMap[session.id] = response.data;
          }
        } catch (error) {
          console.error(`Error fetching reports for session ${session.id}:`, error);
        }
      }
      
      setSessionReports(reportsMap);
    };
    
    if (sessions && sessions.length > 0) {
      fetchReportsForSessions();
    }
  }, [sessions, API]);

  // Fetch medical background data (conditions, medications, allergies)
  React.useEffect(() => {
    const fetchMedicalBackground = async () => {
      if (!baby || !baby.id) return;
      
      try {
        const [conditionsRes, medicationsRes, allergiesRes] = await Promise.all([
          axios.get(`${API}/api/medical-conditions/${baby.id}`).catch(() => ({ data: [] })),
          axios.get(`${API}/api/medications/${baby.id}`).catch(() => ({ data: [] })),
          axios.get(`${API}/api/allergies/${baby.id}`).catch(() => ({ data: [] }))
        ]);
        
        setMedicalBackground({
          conditions: conditionsRes.data || [],
          medications: medicationsRes.data || [],
          allergies: allergiesRes.data || []
        });
      } catch (error) {
        console.error('Error fetching medical background:', error);
      }
    };
    
    fetchMedicalBackground();
  }, [baby, API]);

  // Filter sessions when sessions, month, year, or doctor search changes
  React.useEffect(() => {
    if (sessions.length === 0) {
      setFilteredSessions([]);
      return;
    }

    let filtered = [...sessions];

    // Filter by month and year
    if (selectedMonth !== 'all' || selectedYear !== 'all') {
      filtered = filtered.filter(session => {
        const sessionDate = new Date(session.session_date);
        const sessionMonth = sessionDate.getMonth() + 1; // 1-12
        const sessionYear = sessionDate.getFullYear();

        const monthMatch = selectedMonth === 'all' || parseInt(selectedMonth) === sessionMonth;
        const yearMatch = selectedYear === 'all' || parseInt(selectedYear) === sessionYear;

        return monthMatch && yearMatch;
      });
    }

    // Filter by doctor name
    if (doctorSearchQuery.trim()) {
      filtered = filtered.filter(session => {
        const doctorName = session.doctor_name || session.updated_by || '';
        return doctorName.toLowerCase().includes(doctorSearchQuery.toLowerCase());
      });
    }

    setFilteredSessions(filtered);
  }, [sessions, selectedMonth, selectedYear, doctorSearchQuery]);

  // Get session by ID
  const getSessionById = (sessionId) => {
    return sessions.find(s => s.id === sessionId);
  };

  const handlePrintPreview = (session) => {
    // Directly open prescription in new tab
    if (session && baby) {
      const prescriptionUrl = `/prescription/${session.id}?babyId=${baby.id}`;
      window.open(prescriptionUrl, '_blank');
    }
  };

  const handlePrint = () => {
    try {
      // Open prescription in new tab for printing
      if (printSession && baby) {
        const prescriptionUrl = `/prescription/${printSession.id}?babyId=${baby.id}`;
        window.open(prescriptionUrl, '_blank');
      }
    } catch (error) {
      console.error('Print error:', error);
      alert('Unable to open prescription page. Please try again.');
    }
  };

  // Get follow-up chain recursively
  const getFollowUpChain = (sessionId) => {
    const chain = [];
    let currentId = sessionId;
    
    while (currentId) {
      const session = getSessionById(currentId);
      if (!session) break;
      chain.push(session);
      currentId = session.follow_up_of;
    }
    
    return chain;
  };

  // Get file icon based on extension
  const getFileIcon = (url) => {
    if (!url) return '📄';
    const extension = url.split('.').pop().toLowerCase();
    if (extension === 'pdf') {
      return '📄';
    }
    if (extension === 'dcm' || extension === 'dicom') {
      return '🔬';
    }
    return '🖼️';
  };

  // Get filename from document (handles both string URLs and objects with filename)
  const getFilename = (doc) => {
    // If doc is an object with filename property, use it
    if (typeof doc === 'object' && doc.filename) {
      return doc.filename;
    }
    // Otherwise treat as URL string and extract filename
    const url = typeof doc === 'string' ? doc : (doc.file_path || doc.url);
    if (!url) return 'Unknown file';
    return url.split('/').pop();
  };

  // Get document URL (handles both string URLs and objects)
  const getDocumentUrl = (doc) => {
    return typeof doc === 'string' ? doc : (doc.file_path || doc.url);
  };

  // Helper to safely construct file URLs - handles both absolute S3 URLs and relative paths
  const getSafeFileUrl = (url) => {
    if (!url) return '';
    // If it's already an absolute URL, return as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Otherwise, prepend the API URL
    return `${API}${url}`;
  };

  // Helper function to open S3 or local files with presigned URLs
  const openFileWithPresignedUrl = async (fileUrl, fileName) => {
    if (!fileUrl) {
      console.error('No file URL provided');
      return;
    }
    
    console.log('Opening file:', fileUrl);
    
    // Check if it's an S3 URL (full URL with s3.amazonaws.com)
    if (fileUrl.includes('s3.') && fileUrl.includes('amazonaws.com')) {
      try {
        // Extract S3 key from URL
        const urlParts = fileUrl.split('.amazonaws.com/');
        if (urlParts.length > 1) {
          const s3Key = urlParts[1].split('?')[0]; // Remove any query params
          console.log('Fetching presigned URL for S3 key:', s3Key);
          // Get presigned URL from backend
          const response = await axios.get(`${API}/api/s3-file/${s3Key}`);
          if (response.data && response.data.url) {
            window.open(response.data.url, '_blank');
            return;
          }
        }
      } catch (error) {
        console.error('Error getting presigned URL:', error);
        // Fallback: try opening the URL directly
      }
    }
    
    // For local files (starting with /api/uploads, /uploads, or similar), open via backend
    let url;
    if (fileUrl.startsWith('http')) {
      url = fileUrl;
    } else if (fileUrl.startsWith('/api/')) {
      // File path includes /api/ prefix
      url = `${API}${fileUrl}`;
    } else if (fileUrl.startsWith('/uploads/')) {
      // File path without /api/ prefix - just /uploads/
      url = `${API}${fileUrl}`;
    } else if (fileUrl.startsWith('/')) {
      // Other absolute path
      url = `${API}${fileUrl}`;
    } else {
      // Relative path
      url = `${API}/${fileUrl}`;
    }
    
    console.log('Opening URL:', url);
    window.open(url, '_blank');
  };

  // Check if file is DICOM (for icon display)
  const isDICOMFile = (url) => {
    if (!url) return false;
    const extension = url.split('.').pop().toLowerCase();
    return extension === 'dcm' || extension === 'dicom';
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
          Medical Sessions History
        </h3>
        {!viewOnlyMode && (
          <Button 
            onClick={onAdd}
            className="bg-gray-900 hover:bg-gray-800 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Session
          </Button>
        )}
      </div>
      
      {/* Month, Year, and Doctor Name Filter */}
      {sessions.length > 0 && (
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="flex items-center gap-4 flex-wrap">
            <Label className="text-sm font-medium text-gray-700">Filter by:</Label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                <SelectItem value="1">January</SelectItem>
                <SelectItem value="2">February</SelectItem>
                <SelectItem value="3">March</SelectItem>
                <SelectItem value="4">April</SelectItem>
                <SelectItem value="5">May</SelectItem>
                <SelectItem value="6">June</SelectItem>
                <SelectItem value="7">July</SelectItem>
                <SelectItem value="8">August</SelectItem>
                <SelectItem value="9">September</SelectItem>
                <SelectItem value="10">October</SelectItem>
                <SelectItem value="11">November</SelectItem>
                <SelectItem value="12">December</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {Array.from(new Set(sessions.map(s => new Date(s.session_date).getFullYear())))
                  .filter(year => !isNaN(year) && year > 1900)
                  .sort((a, b) => b - a)
                  .map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1 max-w-xs">
              <Input
                type="text"
                placeholder="Search by doctor name..."
                value={doctorSearchQuery}
                onChange={(e) => setDoctorSearchQuery(e.target.value)}
                className="pl-9"
              />
              <User className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
            {(selectedMonth !== 'all' || selectedYear !== 'all' || doctorSearchQuery.trim()) && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setSelectedMonth('all');
                  setSelectedYear('all');
                  setDoctorSearchQuery('');
                }}
              >
                Clear Filters
              </Button>
            )}
            <span className="text-sm text-gray-600 ml-auto">
              Showing {filteredSessions.length} of {sessions.length} sessions
            </span>
          </div>
        </div>
      )}
      
      <div className="p-6">
        {sessions.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Stethoscope className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-lg font-medium">No medical sessions recorded</p>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-lg font-medium">No sessions found for selected period</p>
            <p className="text-sm mt-1">Try adjusting the filters</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSessions.map((session) => (
              <div 
                key={session.id} 
                className="border border-gray-200 rounded-lg p-5 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => {
                  setViewSession(session);
                  setShowViewDialog(true);
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{formatDate(session.session_date)}</p>
                      <p className="text-sm text-gray-600">{session.doctor_name || 'Doctor'}</p>
                      
                      {/* Follow-up chain display */}
                      {session.follow_up_of && (() => {
                        const followUpChain = getFollowUpChain(session.follow_up_of);
                        return (
                          <div className="mt-1 space-y-1">
                            {followUpChain.length === 1 ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onViewPrevious) onViewPrevious(followUpChain[0]);
                                }}
                                className="flex items-center space-x-1 text-sm text-purple-600 hover:text-purple-800 hover:underline cursor-pointer"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                <span>Follow-up to {formatDate(followUpChain[0].session_date)}</span>
                              </button>
                            ) : (
                              <div className="space-y-1">
                                <div className="flex items-center space-x-1 text-sm text-purple-600">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  <span className="font-medium">Follow-up chain ({followUpChain.length} previous sessions):</span>
                                </div>
                                <div className="ml-5 space-y-1">
                                  {followUpChain.map((prevSession, index) => (
                                    <button
                                      key={prevSession.id}
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (onViewPrevious) onViewPrevious(prevSession);
                                      }}
                                      className="flex items-center space-x-2 text-xs text-purple-600 hover:text-purple-800 hover:underline cursor-pointer"
                                    >
                                      <span className="font-semibold">#{followUpChain.length - index}</span>
                                      <span>{formatDate(prevSession.session_date)} - {prevSession.doctor_name || 'Doctor'}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  
                  {/* SOAP Button - Show if session has AI SOAP notes */}
                  {session.ai_soap_notes && currentUserId && session.updated_by === currentUserId && (
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onViewSOAP) onViewSOAP(session.ai_soap_notes);
                      }}
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 border-purple-200 bg-purple-50"
                      title="View AI SOAP Notes"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      SOAP
                    </Button>
                  )}

                  {!viewOnlyMode && (
                    <div className="flex gap-2">
                      {onFollowUp && (
                        <Button 
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onFollowUp(session);
                          }}
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          title="Create follow-up session"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </Button>
                      )}
                      
                      {/* Show Edit/Delete/Print buttons only if this doctor created the session */}
                      {(() => {
                        const canEdit = currentUserId && session.updated_by === currentUserId;
                        
                        return (
                          <>
                            {canEdit && onEdit && (
                              <Button 
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit(session);
                                }}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title="Edit session"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </Button>
                            )}
                            {canEdit && (
                              <Button 
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePrintPreview(session);
                                }}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                title="Print prescription"
                              >
                                <Printer className="w-4 h-4" />
                              </Button>
                            )}
                          </>
                        );
                      })()}
                      
                      {(() => {
                        const canDelete = currentUserId && session.updated_by === currentUserId;
                        
                        return canDelete && onDelete ? (
                          <Button 
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent card click from triggering
                              onDelete(session.id);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete session"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </Button>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>
                
                <div className="space-y-3 ml-13">
                  {/* Background Section */}
                  {(medicalBackground.conditions.length > 0 || medicalBackground.medications.length > 0 || medicalBackground.allergies.length > 0) && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Background</h4>
                      <div className="text-xs text-gray-700 space-y-1">
                        {medicalBackground.conditions.length > 0 && (
                          <div>
                            <span className="font-medium">Conditions: </span>
                            <div className="ml-2 space-y-0.5">
                              {medicalBackground.conditions.map((c, idx) => (
                                <div key={idx}>
                                  • {c.condition_name}
                                  {c.medication && <span className="text-gray-600"> ({c.medication})</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {medicalBackground.medications.length > 0 && (
                          <div>
                            <span className="font-medium">Medications: </span>
                            <span>{medicalBackground.medications.map(m => m.medication_name).join(', ')}</span>
                          </div>
                        )}
                        {medicalBackground.allergies.length > 0 && (
                          <div>
                            <span className="font-medium">Allergies: </span>
                            <span className="text-red-600 font-medium">{medicalBackground.allergies.map(a => a.allergen).join(', ')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Vitals */}
                  {(session.temperature || session.pulse || session.spo2 || session.blood_pressure || session.bp_systolic || session.height || session.weight) && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Vitals</h4>
                      <div className="text-sm text-gray-800 flex flex-wrap items-center gap-1">
                        {session.temperature && (
                          <>
                            <span>Temp: {session.temperature}°F</span>
                            {getVitalStatus('temp', session.temperature) && (
                              <Badge className={`text-xs px-1 py-0 ${getVitalStatus('temp', session.temperature).color}`}>
                                {getVitalStatus('temp', session.temperature).status}
                              </Badge>
                            )}
                          </>
                        )}
                        {session.temperature && session.pulse && <span> | </span>}
                        {session.pulse && (
                          <>
                            <span>Pulse: {session.pulse} bpm</span>
                            {getVitalStatus('pulse', session.pulse) && (
                              <Badge className={`text-xs px-1 py-0 ${getVitalStatus('pulse', session.pulse).color}`}>
                                {getVitalStatus('pulse', session.pulse).status}
                              </Badge>
                            )}
                          </>
                        )}
                        {(session.temperature || session.pulse) && session.spo2 && <span> | </span>}
                        {session.spo2 && (
                          <>
                            <span>SpO2: {session.spo2}%</span>
                            {getVitalStatus('spo2', session.spo2) && (
                              <Badge className={`text-xs px-1 py-0 ${getVitalStatus('spo2', session.spo2).color}`}>
                                {getVitalStatus('spo2', session.spo2).status}
                              </Badge>
                            )}
                          </>
                        )}
                        {(session.temperature || session.pulse || session.spo2) && (session.blood_pressure || (session.bp_systolic && session.bp_diastolic)) && <span> | </span>}
                        {(session.blood_pressure || (session.bp_systolic && session.bp_diastolic)) && (
                          <>
                            <span>BP: {session.blood_pressure || `${session.bp_systolic}/${session.bp_diastolic}`}</span>
                            {(() => {
                              const bpValue = session.blood_pressure || `${session.bp_systolic}/${session.bp_diastolic}`;
                              if (bpValue && bpValue.includes('/')) {
                                const [sys] = bpValue.split('/');
                                const sysStatus = getVitalStatus('bp_systolic', sys);
                                return sysStatus && (
                                  <Badge className={`text-xs px-1 py-0 ${sysStatus.color}`}>
                                    {sysStatus.status}
                                  </Badge>
                                );
                              }
                              return null;
                            })()}
                          </>
                        )}
                        <br />
                        {session.height && <span>Height: {session.height} cm</span>}
                        {session.height && session.weight && <span> | </span>}
                        {session.weight && <span>Weight: {session.weight} kg</span>}
                        {(() => {
                          const bmiResult = baby?.date_of_birth && baby?.gender && session.height && session.weight
                            ? calculateBMI(parseFloat(session.weight), parseFloat(session.height), baby.date_of_birth, baby.gender, '')
                            : null;
                          if (bmiResult && bmiResult.bmi) {
                            return (
                              <>
                                <span> | </span>
                                <span>BMI: {bmiResult.bmi} </span>
                                <Badge className={`${getBMIBadgeClasses(bmiResult.color)} border text-xs ml-1`}>
                                  ({bmiResult.status})
                                </Badge>
                              </>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  )}
                  
                  {/* Chief Complaints */}
                  {(session.chief_complaints || session.chief_complaint || session.symptoms) && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Chief Complaints</h4>
                      <p className="text-sm text-gray-800 leading-relaxed">{session.chief_complaints || session.chief_complaint || session.symptoms}</p>
                    </div>
                  )}
                  
                  {/* Assessment */}
                  {session.assessment && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Assessment</h4>
                      <p className="text-sm text-gray-800 leading-relaxed">{session.assessment}</p>
                    </div>
                  )}
                  
                  {/* Tests */}
                  {session.tests && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Tests Recommended</h4>
                      <p className="text-sm text-gray-800 leading-relaxed">{session.tests}</p>
                    </div>
                  )}
                  
                  {/* Diagnosis */}
                  {session.diagnosis && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Diagnosis</h4>
                      <div className="text-sm text-gray-800 leading-relaxed space-y-1">
                        {session.diagnosis.split('\n').filter(line => line.trim()).map((line, idx) => (
                          <div key={idx} className="text-blue-700">{line}</div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Medication Prescribed */}
                  {session.medication && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-1 flex items-center space-x-1">
                        <FileText className="w-3 h-3" />
                        <span>Medication Prescribed</span>
                      </h4>
                      <p className="text-sm text-gray-800 leading-relaxed">{cleanMedicationForDisplay(session.medication)}</p>
                    </div>
                  )}
                  
                  {/* Additional Tests */}
                  {session.additional_tests && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Additional Tests</h4>
                      <p className="text-sm text-gray-800 leading-relaxed">{session.additional_tests}</p>
                    </div>
                  )}
                  
                  {/* Advice */}
                  {session.notes && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Advice</h4>
                      <p className="text-sm text-gray-600 leading-relaxed italic">{session.notes}</p>
                    </div>
                  )}

                  {/* Next Follow-up */}
                  {session.next_followup && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Next Follow-up</h4>
                      <p className="text-sm text-gray-800 font-medium">
                        {(() => {
                          // Convert YYYY-MM-DD to DD/MM/YYYY
                          const date = new Date(session.next_followup);
                          const day = String(date.getDate()).padStart(2, '0');
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const year = date.getFullYear();
                          return `${day}/${month}/${year}`;
                        })()}
                      </p>
                    </div>
                  )}

                  {/* Display uploaded documents and reports */}
                  {((session.documents && session.documents.length > 0) || (sessionReports[session.id] && sessionReports[session.id].length > 0)) && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-2 flex items-center space-x-1">
                        <FileText className="w-3 h-3" />
                        <span>Attached Documents & Reports</span>
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {/* Show existing documents (uploaded in live session) */}
                        {session.documents && session.documents.map((doc, index) => {
                          const docUrl = getDocumentUrl(doc);
                          const filename = getFilename(doc);
                          const isObject = typeof doc === 'object';
                          const hasAnalysis = isObject && (doc.analysis_file_url || doc.abnormal_count > 0 || doc.borderline_count > 0);
                          
                          return (
                            <div key={`doc-${index}`} className="flex flex-col gap-1.5">
                              {/* AI Analysis file - shown ABOVE original report if available */}
                              {hasAnalysis && (
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openFileWithPresignedUrl(doc.analysis_file_url, 'Analysis Report'); }}
                                    className="flex items-center gap-1.5 px-2 py-1 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded text-xs font-medium transition-colors cursor-pointer"
                                    title="View AI Analysis - Abnormal & Borderline Values"
                                  >
                                    <FileText className="w-3 h-3 text-purple-600" />
                                    <span className="text-purple-700">Analysis</span>
                                    {(doc.abnormal_count > 0 || doc.borderline_count > 0) && (
                                      <span className="flex gap-1">
                                        {doc.abnormal_count > 0 && (
                                          <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-bold">
                                            {doc.abnormal_count} Abnormal
                                          </span>
                                        )}
                                        {doc.borderline_count > 0 && (
                                          <span className="text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded font-bold">
                                            {doc.borderline_count} Borderline
                                          </span>
                                        )}
                                      </span>
                                    )}
                                  </button>
                                  {/* Re-analyze button for live session uploads */}
                                  {doc.id && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        setReanalyzingReports(prev => ({...prev, [doc.id]: true}));
                                        try {
                                          // Use the reanalyze endpoint for saved reports
                                          await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/report-uploads/${doc.id}/reanalyze`);
                                          // Refresh the session to get updated analysis
                                          const sessionResponse = await axios.get(`${API}/api/medical-sessions/baby/${selectedBaby.id}`);
                                          const allSessions = Array.isArray(sessionResponse.data) ? sessionResponse.data : [];
                                          setSessions(allSessions);
                                        } catch (error) {
                                          console.error('Re-analyze error:', error);
                                        } finally {
                                          setReanalyzingReports(prev => ({...prev, [doc.id]: false}));
                                        }
                                      }}
                                      disabled={reanalyzingReports[doc.id]}
                                      className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors disabled:opacity-50"
                                      title="Re-analyze report"
                                    >
                                      <svg className={`w-3 h-3 ${reanalyzingReports[doc.id] ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                      </svg>
                                      <span>{reanalyzingReports[doc.id] ? 'Analyzing...' : 'Re-analyze'}</span>
                                    </button>
                                  )}
                                </div>
                              )}
                              {/* Original document/report file */}
                              <button
                                onClick={(e) => { e.stopPropagation(); openFileWithPresignedUrl(docUrl, filename); }}
                                className="flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors max-w-xs cursor-pointer"
                                title={filename}
                              >
                                <span className="text-lg flex-shrink-0">{getFileIcon(docUrl)}</span>
                                <span className="text-xs text-gray-700 truncate">{filename}</span>
                              </button>
                            </div>
                          );
                        })}
                        {/* Show uploaded reports from Reports Upload page */}
                        {sessionReports[session.id] && sessionReports[session.id].map((report) => (
                          <div key={`report-${report.id}`} className="flex flex-col gap-1.5">
                            {/* AI Analysis file - shown ABOVE original report */}
                            {report.analysis_file_url && (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); openFileWithPresignedUrl(report.analysis_file_url, 'Analysis Report'); }}
                                  className="flex items-center gap-1.5 px-2 py-1 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded text-xs font-medium transition-colors cursor-pointer"
                                  title="View AI Analysis - Abnormal & Borderline Values"
                                >
                                  <FileText className="w-3 h-3 text-purple-600" />
                                  <span className="text-purple-700">Analysis</span>
                                  {(report.abnormal_count > 0 || report.borderline_count > 0) && (
                                    <span className="flex gap-1">
                                      {report.abnormal_count > 0 && (
                                        <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-bold">
                                          {report.abnormal_count} Abnormal
                                        </span>
                                      )}
                                      {report.borderline_count > 0 && (
                                        <span className="text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded font-bold">
                                          {report.borderline_count} Borderline
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </button>
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    setReanalyzingReports(prev => ({...prev, [report.id]: true}));
                                    try {
                                      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/report-uploads/${report.id}/reanalyze`);
                                      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/report-uploads/session/${session.id}`);
                                      const reportsMap = {...sessionReports};
                                      reportsMap[session.id] = response.data;
                                      setSessionReports(reportsMap);
                                    } catch (error) {
                                      console.error('Re-analyze error:', error);
                                    } finally {
                                      setReanalyzingReports(prev => ({...prev, [report.id]: false}));
                                    }
                                  }}
                                  disabled={reanalyzingReports[report.id]}
                                  className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors disabled:opacity-50"
                                  title="Re-analyze report"
                                >
                                  <svg className={`w-3 h-3 ${reanalyzingReports[report.id] ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  <span>{reanalyzingReports[report.id] ? 'Analyzing...' : 'Re-analyze'}</span>
                                </button>
                              </div>
                            )}
                            {/* Original report file */}
                            <button
                              onClick={(e) => { e.stopPropagation(); openFileWithPresignedUrl(report.report_url, report.test_name || 'Report'); }}
                              className="flex items-center space-x-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors max-w-xs cursor-pointer"
                              title={`${report.test_name} - ${report.notes || 'Report'}`}
                            >
                              <FileText className="w-4 h-4 text-blue-600" />
                              <div className="flex flex-col">
                                <span className="text-xs text-blue-900 font-medium">{report.test_name}</span>
                                {report.notes && <span className="text-xs text-blue-700 truncate">{report.notes}</span>}
                              </div>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Updated By */}
                  {session.doctor_name && (
                    <p className="text-xs text-purple-600 mt-2">
                      Updated by {session.doctor_name}
                      {session.hospital_name && ` - ${session.hospital_name}`}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Print Preview Modal */}
      <Dialog open={showPrintPreview} onOpenChange={setShowPrintPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="print:hidden">
            <DialogTitle>Prescription Preview</DialogTitle>
            <DialogDescription>
              Review the prescription before printing. Click Print to open the print dialog directly.
            </DialogDescription>
          </DialogHeader>
          
          {printSession && baby ? (
            <div id="prescription-print-doctor" className="bg-white p-8 mx-auto max-w-full" style={{ maxWidth: '210mm' }}>
              {/* Print-only Styles */}
              <style>{`
                @media print {
                  /* Page settings - no margins for full bleed */
                  @page {
                    size: A4 portrait;
                    margin: 0;
                  }
                  
                  /* Hide everything on the page */
                  body * {
                    display: none !important;
                  }
                  
                  /* Show only prescription and its children */
                  #prescription-print-doctor,
                  #prescription-print-doctor * {
                    display: block !important;
                    visibility: visible !important;
                  }
                  
                  /* Position prescription to fill A4 */
                  #prescription-print-doctor {
                    position: fixed !important;
                    left: 0 !important;
                    top: 0 !important;
                    width: 210mm !important;
                    min-height: 297mm !important;
                    padding: 20mm 15mm !important;
                    margin: 0 !important;
                    background: white !important;
                    z-index: 99999 !important;
                    font-size: 12pt !important;
                  }
                  
                  /* Ensure flex layout works in print */
                  #prescription-print-doctor > div:first-child {
                    display: flex !important;
                  }
                  
                  #prescription-print-doctor .flex {
                    display: flex !important;
                  }
                  
                  #prescription-print-doctor .justify-between {
                    justify-content: space-between !important;
                  }
                  
                  #prescription-print-doctor .items-start {
                    align-items: flex-start !important;
                  }
                  
                  /* Ensure SVG (QR code) displays */
                  #prescription-print-doctor svg,
                  #prescription-print-doctor svg * {
                    display: block !important;
                    visibility: visible !important;
                  }
                  
                  /* Table styles for print */
                  #prescription-print-doctor table {
                    display: table !important;
                    width: 100% !important;
                    border-collapse: collapse !important;
                    page-break-inside: avoid !important;
                    font-size: 14pt !important;
                  }
                  
                  #prescription-print-doctor thead {
                    display: table-header-group !important;
                  }
                  
                  #prescription-print-doctor tbody {
                    display: table-row-group !important;
                  }
                  
                  #prescription-print-doctor tr {
                    display: table-row !important;
                  }
                  
                  #prescription-print-doctor th,
                  #prescription-print-doctor td {
                    display: table-cell !important;
                    border: 1px solid black !important;
                    padding: 10px !important;
                    font-size: 14pt !important;
                  }
                  
                  #prescription-print-doctor th {
                    background-color: #f3f4f6 !important;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                  }
                  
                  /* Force text to be visible */
                  #prescription-print-doctor,
                  #prescription-print-doctor * {
                    color: black !important;
                    opacity: 1 !important;
                  }
                  
                  /* Force SVG and all fills to print - CRITICAL */
                  #prescription-print-doctor svg,
                  #prescription-print-doctor svg *,
                  #prescription-print-doctor svg circle {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    color-adjust: exact !important;
                  }
                  
                  /* Force ALL backgrounds to print */
                  #prescription-print-doctor *,
                  #prescription-print-doctor div {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    color-adjust: exact !important;
                  }
                  
                  /* Borders for print */
                  #prescription-print-doctor .border-b {
                    border-bottom: 1px solid black !important;
                  }
                  
                  /* Hide all dialogs, modals, overlays */
                  [role="dialog"],
                  [role="alertdialog"],
                  .fixed,
                  button:not(#prescription-print-doctor button) {
                    display: none !important;
                  }
                }
                
                /* Portrait mode compatibility for all screens */
                @media screen and (max-width: 768px) {
                  #prescription-print-doctor {
                    max-width: 100%;
                    overflow-x: auto;
                  }
                }
              `}</style>

              {/* Top Section - Date and Doctor (Top Right) */}
              <div className="flex justify-end mb-6 text-sm print:text-base">
                <div className="text-right">
                  <div className="mb-1">
                    <strong>Date:</strong> {(() => {
                      // Ensure date is in DD/MM/YYYY format
                      if (!printSession.session_date) return 'N/A';
                      
                      if (printSession.session_date.includes('/')) {
                        return printSession.session_date;
                      } else if (printSession.session_date.includes('-')) {
                        const parts = printSession.session_date.split('-');
                        if (parts[0].length === 4) {
                          const [year, month, day] = parts;
                          return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
                        } else {
                          return printSession.session_date.replace(/-/g, '/');
                        }
                      }
                      return printSession.session_date;
                    })()}
                  </div>
                  <div className="mb-1">
                    <strong>Doctor:</strong> <span className="font-bold">{printSession.doctor_name}</span>
                  </div>
                </div>
              </div>

              {/* Baby Info and QR Code - Parallel, Below Date/Doctor */}
              <div className="flex justify-between items-start mb-4 pb-4 border-b border-gray-300 print:border-black">
                {/* Baby Information - Left Side */}
                <div className="flex-1">
                  <div className="text-sm font-semibold mb-1 text-gray-700 print:text-base">
                    {baby.short_id || baby.id?.substring(0, 8).toUpperCase()}
                  </div>
                  <div className="text-lg font-bold mb-1 print:text-xl">
                    {baby.name} ({(() => {
                      const genderOrSex = baby.gender || baby.sex || '';
                      const normalized = genderOrSex.toLowerCase();
                      if (normalized === 'male' || normalized === 'm') return 'M';
                      if (normalized === 'female' || normalized === 'f') return 'F';
                      return genderOrSex?.[0]?.toUpperCase() || 'N/A';
                    })()})
                  </div>
                  <div className="text-sm text-gray-600 print:text-base print:text-black">
                    <strong>Age:</strong> {(() => {
                      try {
                        if (!baby.date_of_birth) return 'N/A';
                        
                        let dob;
                        if (baby.date_of_birth.includes('/')) {
                          const [day, month, year] = baby.date_of_birth.split('/').map(s => parseInt(s, 10));
                          dob = new Date(year, month - 1, day);
                        } else if (baby.date_of_birth.includes('-')) {
                          const parts = baby.date_of_birth.split('-').map(s => parseInt(s, 10));
                          if (parts[0] > 1000) {
                            dob = new Date(parts[0], parts[1] - 1, parts[2]);
                          } else {
                            dob = new Date(parts[2], parts[1] - 1, parts[0]);
                          }
                        } else {
                          dob = new Date(baby.date_of_birth);
                        }
                        
                        if (isNaN(dob.getTime())) {
                          console.error('Invalid date:', baby.date_of_birth);
                          return 'N/A';
                        }
                        
                        const today = new Date();
                        let years = today.getFullYear() - dob.getFullYear();
                        let months = today.getMonth() - dob.getMonth();
                        let days = today.getDate() - dob.getDate();
                        
                        // Adjust for negative days
                        if (days < 0) {
                          months--;
                          const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
                          days += lastMonth.getDate();
                        }
                        
                        // Adjust for negative months
                        if (months < 0) {
                          years--;
                          months += 12;
                        }
                        
                        if (years < 0 || months < 0 || days < 0) return 'N/A';
                        
                        // Format: "7 years, 1 month, 20 days"
                        const parts = [];
                        if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`);
                        if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`);
                        if (days > 0 || parts.length === 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
                        
                        return parts.join(', ');
                      } catch (error) {
                        console.error('Age calculation error:', error, baby.date_of_birth);
                        return 'N/A';
                      }
                    })()}
                  </div>
                </div>
                
                {/* QR Code - Right Side, parallel to baby info */}
                <div className="ml-8">
                  <QRCodeSVG 
                    value={`${window.location.origin}/prescription/${printSession.id}?babyId=${baby.id}`}
                    size={100}
                    level="M"
                  />
                </div>
              </div>

              {/* Prescription Symbol */}
              <div className="text-4xl font-serif mb-4 print:text-5xl">℞</div>

              {/* Vitals */}
              {(printSession.height || printSession.weight || printSession.bp_systolic || printSession.bp_diastolic || printSession.pulse || printSession.spo2) && (
                <div className="mb-4">
                  <div className="font-semibold mb-1 print:text-base">Vitals:</div>
                  <div className="text-sm print:text-base">
                    {printSession.height && <span>Height: {printSession.height} cm</span>}
                    {printSession.height && printSession.weight && <span> | </span>}
                    {printSession.weight && <span>Weight: {printSession.weight} kg</span>}
                    {(() => {
                      const bmiResult = baby?.date_of_birth && baby?.gender && printSession.height && printSession.weight
                        ? calculateBMI(parseFloat(printSession.weight), parseFloat(printSession.height), baby.date_of_birth, baby.gender, '')
                        : null;
                      if (bmiResult && bmiResult.bmi) {
                        return (
                          <>
                            <span> | </span>
                            <span>BMI: {bmiResult.bmi} ({bmiResult.status})</span>
                          </>
                        );
                      }
                      return null;
                    })()}
                    {(printSession.height || printSession.weight) && (printSession.bp_systolic || printSession.pulse || printSession.spo2) && <br />}
                    {(printSession.bp_systolic && printSession.bp_diastolic) && <span>BP: {printSession.bp_systolic}/{printSession.bp_diastolic}</span>}
                    {(printSession.bp_systolic && printSession.bp_diastolic) && printSession.pulse && <span> | </span>}
                    {printSession.pulse && <span>Pulse: {printSession.pulse} bpm</span>}
                    {(printSession.pulse || (printSession.bp_systolic && printSession.bp_diastolic)) && printSession.spo2 && <span> | </span>}
                    {printSession.spo2 && <span>SpO2: {printSession.spo2}%</span>}
                  </div>
                </div>
              )}

              {/* Temperature */}
              {printSession.temperature && (
                <div className="mb-4">
                  <div className="font-semibold mb-1 print:text-base">Temperature:</div>
                  <div className="text-sm print:text-base">{printSession.temperature}°F</div>
                </div>
              )}

              {/* Chief Complaints */}
              {(printSession.chief_complaints || printSession.symptoms) && (
                <div className="mb-4">
                  <div className="font-semibold mb-1 print:text-base">Chief Complaints:</div>
                  <div className="text-sm print:text-base">{printSession.chief_complaints || printSession.symptoms}</div>
                </div>
              )}

              {/* Assessment */}
              {printSession.assessment && (
                <div className="mb-4">
                  <div className="font-semibold mb-1 print:text-base">Assessment:</div>
                  <div className="text-sm print:text-base">{printSession.assessment}</div>
                </div>
              )}

              {/* Tests */}
              {printSession.tests && (
                <div className="mb-4">
                  <div className="font-semibold mb-1 print:text-base">Tests Recommended:</div>
                  <div className="text-sm print:text-base">{printSession.tests}</div>
                </div>
              )}

              {/* Diagnosis */}
              {printSession.diagnosis && (
                <div className="mb-4">
                  <div className="font-semibold mb-1 print:text-base">Diagnosis:</div>
                  <div className="text-sm print:text-base space-y-1">
                    {printSession.diagnosis.split('\n').filter(line => line.trim()).map((line, idx) => (
                      <div key={idx}>{line}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Medication Table */}
              <div className="mb-6">
                <div className="font-semibold mb-2 print:text-base">Medications Prescribed:</div>
                <table className="w-full border-collapse border border-gray-300 print:border-black">
                  <thead>
                    <tr className="bg-gray-100 print:bg-gray-200">
                      <th className="border border-gray-300 print:border-black px-3 py-2 text-left text-sm print:text-base">#</th>
                      <th className="border border-gray-300 print:border-black px-3 py-2 text-left text-sm print:text-base">Medicine Name</th>
                      <th className="border border-gray-300 print:border-black px-3 py-2 text-left text-sm print:text-base">Dosage</th>
                      <th className="border border-gray-300 print:border-black px-3 py-2 text-left text-sm print:text-base">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {printSession.medication && printSession.medication.split(' | ').map((med, index) => {
                      // Parse medication string
                      let name = med;
                      let genericName = '';
                      let treatmentFor = '';
                      let dosage = '';
                      let duration = '';
                      
                      // Extract generic name if present
                      const genericMatch = name.match(/\[Generic:\s*([^\]]+)\]/);
                      if (genericMatch) {
                        genericName = genericMatch[1].trim();
                        name = name.replace(genericMatch[0], '').trim();
                      }
                      
                      // Extract treatment for if present
                      const treatmentMatch = name.match(/\[For:\s*([^\]]+)\]/);
                      if (treatmentMatch) {
                        treatmentFor = treatmentMatch[1].trim();
                        name = name.replace(treatmentMatch[0], '').trim();
                      }
                      
                      // Extract duration
                      const durationMatch = name.match(/for\s+(.+)$/i);
                      if (durationMatch) {
                        duration = durationMatch[1];
                        name = name.substring(0, durationMatch.index).trim();
                      }
                      
                      // Extract timing info
                      let timing = '';
                      let timeOfDayArray = [];
                      const timingMatch = name.match(/^(.+?)\s*\((.+?)\)(?:\s+-\s+(.+))?$/);
                      if (timingMatch) {
                        name = timingMatch[1];
                        timing = timingMatch[2]; // Before Food / After Food
                        const timeOfDayStr = timingMatch[3] || ''; // Morning, Night, etc.
                        if (timeOfDayStr) {
                          timeOfDayArray = timeOfDayStr.split(',').map(t => t.trim());
                        }
                      }
                      
                      return (
                        <tr key={index}>
                          <td className="border border-gray-300 print:border-black px-3 py-2 text-sm print:text-base">{index + 1}</td>
                          <td className="border border-gray-300 print:border-black px-3 py-2 text-sm print:text-base">
                            <div>{name}</div>
                            {(genericName || treatmentFor) && (
                              <div className="text-xs text-gray-600 print:text-gray-700 mt-0.5">
                                {genericName && <span className="italic">Generic: {genericName}</span>}
                                {genericName && treatmentFor && <span className="mx-1">•</span>}
                                {treatmentFor && <span>For: {treatmentFor}</span>}
                              </div>
                            )}
                          </td>
                          <td className="border border-gray-300 print:border-black px-3 py-2 text-sm print:text-base">
                            {timeOfDayArray.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                {/* Dots Row - Using Unicode characters that ALWAYS print */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '2px', fontSize: '16px', lineHeight: '1', color: '#000', fontFamily: 'Arial, sans-serif' }}>
                                  {['Morning', 'Afternoon', 'Evening', 'Night'].map((timeSlot) => {
                                    const isSelected = timeOfDayArray.includes(timeSlot);
                                    return (
                                      <span key={timeSlot}>
                                        {isSelected ? '●' : '○'}
                                      </span>
                                    );
                                  })}
                                </div>
                                {/* Labels Row - Horizontal */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', fontSize: '10px', fontWeight: '600', color: '#000', marginTop: '2px' }}>
                                  <span style={{ width: '16px', textAlign: 'center' }}>M</span>
                                  <span style={{ width: '16px', textAlign: 'center' }}>A</span>
                                  <span style={{ width: '16px', textAlign: 'center' }}>E</span>
                                  <span style={{ width: '16px', textAlign: 'center' }}>N</span>
                                </div>
                                {/* Timing (Before/After Food) */}
                                {timing && <div style={{ fontSize: '11px', textAlign: 'center', marginTop: '4px', color: '#000' }}>({timing})</div>}
                              </div>
                            ) : (
                              <span>{timing ? `(${timing})` : ''}</span>
                            )}
                          </td>
                          <td className="border border-gray-300 print:border-black px-3 py-2 text-sm print:text-base">{duration}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Additional Tests */}
              {printSession.additional_tests && (
                <div className="mb-4">
                  <div className="font-semibold mb-1 print:text-base">Additional Tests:</div>
                  <div className="text-sm print:text-base">{printSession.additional_tests}</div>
                </div>
              )}

              {/* Advice */}
              {printSession.notes && (
                <div className="mb-4">
                  <div className="font-semibold mb-1 print:text-base">Advice:</div>
                  <div className="text-sm print:text-base">{printSession.notes}</div>
                </div>
              )}

              {/* Next Follow-up */}
              {printSession.next_followup && (
                <div className="mb-4">
                  <div className="font-semibold mb-1 print:text-base">Next Follow-up:</div>
                  <div className="text-sm print:text-base">
                    {(() => {
                      // Convert YYYY-MM-DD to DD/MM/YYYY
                      const date = new Date(printSession.next_followup);
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    })()}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="mt-8 pt-4 border-t border-gray-300 print:border-black text-xs print:text-sm text-gray-600 print:text-black text-center">
                <p>This is a computer-generated prescription</p>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center text-red-600">
              <p className="text-lg font-semibold mb-2">Unable to load prescription data</p>
              <p className="text-sm">
                {!printSession && 'No session selected'}
                {!baby && printSession && 'Baby information not available'}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-4 print:hidden">
            <Button variant="outline" onClick={() => setShowPrintPreview(false)}>
              Close
            </Button>
            <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Session Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Medical Session Details</DialogTitle>
            <DialogDescription>
              View complete details of this medical session including vitals, diagnosis, and prescriptions.
            </DialogDescription>
          </DialogHeader>
          
          {viewSession && (
            <div className="space-y-4">
              {/* Date and Doctor */}
              <div className="flex items-center justify-between pb-4 border-b">
                <div>
                  <p className="text-lg font-semibold">{formatDate(viewSession.session_date)}</p>
                  <p className="text-sm text-gray-600">{viewSession.doctor_name || 'Doctor'}</p>
                </div>
                {/* SOAP Button in popup - only for creator doctor */}
                {viewSession.ai_soap_notes && currentUserId && viewSession.updated_by === currentUserId && onViewSOAP && (
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => onViewSOAP(viewSession.ai_soap_notes)}
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 border-purple-200 bg-purple-50"
                    title="View AI SOAP Notes"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    View SOAP Notes
                  </Button>
                )}
              </div>

              {/* Background Section */}
              {(medicalBackground.conditions.length > 0 || medicalBackground.medications.length > 0 || medicalBackground.allergies.length > 0) && (
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Background</h4>
                  <div className="text-sm text-gray-700 space-y-2">
                    {medicalBackground.conditions.length > 0 && (
                      <div>
                        <span className="font-medium">Medical Conditions: </span>
                        <div className="ml-4 mt-1 space-y-1">
                          {medicalBackground.conditions.map((c, idx) => (
                            <div key={idx}>
                              • {c.condition_name}
                              {c.medication && <span className="text-gray-600"> - Medication: {c.medication}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {medicalBackground.medications.length > 0 && (
                      <div>
                        <span className="font-medium">Current Medications: </span>
                        <span>{medicalBackground.medications.map(m => m.medication_name).join(', ')}</span>
                      </div>
                    )}
                    {medicalBackground.allergies.length > 0 && (
                      <div>
                        <span className="font-medium">Allergies: </span>
                        <span className="text-red-600 font-medium">{medicalBackground.allergies.map(a => a.allergen).join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Vitals */}
              {(viewSession.temperature || viewSession.pulse || viewSession.spo2 || viewSession.blood_pressure || viewSession.bp_systolic || viewSession.height || viewSession.weight) && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Vitals</h4>
                  <div className="text-sm text-gray-700 flex flex-wrap items-center gap-1">
                    {viewSession.temperature && (
                      <>
                        <span>Temp: {viewSession.temperature}°F</span>
                        {getVitalStatus('temp', viewSession.temperature) && (
                          <Badge className={`text-xs px-1 py-0 ${getVitalStatus('temp', viewSession.temperature).color}`}>
                            {getVitalStatus('temp', viewSession.temperature).status}
                          </Badge>
                        )}
                      </>
                    )}
                    {viewSession.temperature && viewSession.pulse && <span> | </span>}
                    {viewSession.pulse && (
                      <>
                        <span>Pulse: {viewSession.pulse} bpm</span>
                        {getVitalStatus('pulse', viewSession.pulse) && (
                          <Badge className={`text-xs px-1 py-0 ${getVitalStatus('pulse', viewSession.pulse).color}`}>
                            {getVitalStatus('pulse', viewSession.pulse).status}
                          </Badge>
                        )}
                      </>
                    )}
                    {(viewSession.temperature || viewSession.pulse) && viewSession.spo2 && <span> | </span>}
                    {viewSession.spo2 && (
                      <>
                        <span>SpO2: {viewSession.spo2}%</span>
                        {getVitalStatus('spo2', viewSession.spo2) && (
                          <Badge className={`text-xs px-1 py-0 ${getVitalStatus('spo2', viewSession.spo2).color}`}>
                            {getVitalStatus('spo2', viewSession.spo2).status}
                          </Badge>
                        )}
                      </>
                    )}
                    {(viewSession.temperature || viewSession.pulse || viewSession.spo2) && (viewSession.blood_pressure || (viewSession.bp_systolic && viewSession.bp_diastolic)) && <span> | </span>}
                    {(viewSession.blood_pressure || (viewSession.bp_systolic && viewSession.bp_diastolic)) && (
                      <>
                        <span>BP: {viewSession.blood_pressure || `${viewSession.bp_systolic}/${viewSession.bp_diastolic}`}</span>
                        {(() => {
                          const bpValue = viewSession.blood_pressure || `${viewSession.bp_systolic}/${viewSession.bp_diastolic}`;
                          if (bpValue && bpValue.includes('/')) {
                            const [sys] = bpValue.split('/');
                            const sysStatus = getVitalStatus('bp_systolic', sys);
                            return sysStatus && (
                              <Badge className={`text-xs px-1 py-0 ${sysStatus.color}`}>
                                {sysStatus.status}
                              </Badge>
                            );
                          }
                          return null;
                        })()}
                      </>
                    )}
                    <br />
                    {viewSession.height && <span>Height: {viewSession.height} cm</span>}
                    {viewSession.height && viewSession.weight && <span> | </span>}
                    {viewSession.weight && <span>Weight: {viewSession.weight} kg</span>}
                    {(() => {
                      const bmiResult = baby?.date_of_birth && baby?.gender && viewSession.height && viewSession.weight
                        ? calculateBMI(parseFloat(viewSession.weight), parseFloat(viewSession.height), baby.date_of_birth, baby.gender, '')
                        : null;
                      if (bmiResult && bmiResult.bmi) {
                        return (
                          <>
                            <span> | </span>
                            <span>BMI: {bmiResult.bmi} </span>
                            <Badge className={`${getBMIBadgeClasses(bmiResult.color)} border text-xs ml-1`}>
                              ({bmiResult.status})
                            </Badge>
                          </>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              )}

              {/* Chief Complaints */}
              {(viewSession.chief_complaints || viewSession.chief_complaint || viewSession.symptoms) && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Chief Complaints</h4>
                  <p className="text-sm text-gray-700">{viewSession.chief_complaints || viewSession.chief_complaint || viewSession.symptoms}</p>
                </div>
              )}

              {/* Assessment */}
              {viewSession.assessment && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Assessment</h4>
                  <p className="text-sm text-gray-700">{viewSession.assessment}</p>
                </div>
              )}

              {/* Tests */}
              {viewSession.tests && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Tests Recommended</h4>
                  <p className="text-sm text-gray-700">{viewSession.tests}</p>
                </div>
              )}

              {/* Diagnosis */}
              {viewSession.diagnosis && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Diagnosis</h4>
                  <div className="text-sm text-gray-700 space-y-1">
                    {viewSession.diagnosis.split('\n').filter(line => line.trim()).map((line, idx) => (
                      <div key={idx}>{line}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Medication */}
              {viewSession.medication && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Medication Prescribed</h4>
                  <div className="text-sm text-gray-700 space-y-1">
                    {cleanMedicationForDisplay(viewSession.medication).split(' | ').map((med, idx) => (
                      <div key={idx} className="flex items-start">
                        <span className="text-gray-400 mr-2">{idx + 1}.</span>
                        <span>{med}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Tests */}
              {viewSession.additional_tests && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Additional Tests</h4>
                  <p className="text-sm text-gray-700">{viewSession.additional_tests}</p>
                </div>
              )}

              {/* Advice */}
              {viewSession.notes && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Advice</h4>
                  <p className="text-sm text-gray-700">{viewSession.notes}</p>
                </div>
              )}

              {/* Attached Documents & Reports */}
              {((viewSession.documents && viewSession.documents.length > 0) || (sessionReports[viewSession.id] && sessionReports[viewSession.id].length > 0)) && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Attached Documents & Reports</h4>
                  <div className="flex flex-wrap gap-2">
                    {/* Show existing documents (uploaded in live session) */}
                    {viewSession.documents && viewSession.documents.map((doc, index) => {
                      const docUrl = getDocumentUrl(doc);
                      const filename = getFilename(doc);
                      const isObject = typeof doc === 'object';
                      const hasAnalysis = isObject && (doc.analysis_file_url || doc.abnormal_count > 0 || doc.borderline_count > 0);
                      
                      return (
                        <div key={`doc-${index}`} className="flex flex-col gap-1.5">
                          {/* AI Analysis file - shown ABOVE original report if available */}
                          {hasAnalysis && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openFileWithPresignedUrl(doc.analysis_file_url, 'AI Analysis')}
                                className="flex items-center gap-1.5 px-3 py-2 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg text-sm font-medium transition-colors cursor-pointer"
                                title="View AI Analysis - Abnormal & Borderline Values"
                              >
                                <FileText className="w-4 h-4 text-purple-600" />
                                <span className="text-purple-700">AI Analysis</span>
                                {(doc.abnormal_count > 0 || doc.borderline_count > 0) && (
                                  <span className="flex gap-1.5">
                                    {doc.abnormal_count > 0 && (
                                      <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded font-bold">
                                        {doc.abnormal_count} Abnormal
                                      </span>
                                    )}
                                    {doc.borderline_count > 0 && (
                                      <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded font-bold">
                                        {doc.borderline_count} Borderline
                                      </span>
                                    )}
                                  </span>
                                )}
                              </button>
                              {/* Re-analyze button for live session uploads */}
                              {doc.id && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    setReanalyzingReports(prev => ({...prev, [doc.id]: true}));
                                    try {
                                      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/report-uploads/${doc.id}/reanalyze`);
                                      // Refresh the session to get updated analysis
                                      const sessionResponse = await axios.get(`${API}/api/medical-sessions/baby/${selectedBaby.id}`);
                                      const allSessions = Array.isArray(sessionResponse.data) ? sessionResponse.data : [];
                                      setSessions(allSessions);
                                      // Also update viewSession
                                      const updatedSession = allSessions.find(s => s.id === viewSession.id);
                                      if (updatedSession) {
                                        setViewSession(updatedSession);
                                      }
                                    } catch (error) {
                                      console.error('Re-analyze error:', error);
                                    } finally {
                                      setReanalyzingReports(prev => ({...prev, [doc.id]: false}));
                                    }
                                  }}
                                  disabled={reanalyzingReports[doc.id]}
                                  className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors disabled:opacity-50"
                                  title="Re-analyze report"
                                >
                                  <svg className={`w-3 h-3 ${reanalyzingReports[doc.id] ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  <span>{reanalyzingReports[doc.id] ? 'Analyzing...' : 'Re-analyze'}</span>
                                </button>
                              )}
                            </div>
                          )}
                          {/* Original document/report file */}
                          <button
                            onClick={() => openFileWithPresignedUrl(docUrl, filename)}
                            className="flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors max-w-md cursor-pointer"
                            title={filename}
                          >
                            <span className="text-lg flex-shrink-0">{getFileIcon(docUrl)}</span>
                            <span className="text-sm text-gray-700 truncate">{filename}</span>
                          </button>
                        </div>
                      );
                    })}
                    {/* Show uploaded reports from Reports Upload page */}
                    {sessionReports[viewSession.id] && sessionReports[viewSession.id].map((report) => (
                      <div key={`report-${report.id}`} className="flex flex-col gap-1.5">
                        {/* AI Analysis file - shown ABOVE original report */}
                        {report.analysis_file_url && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); openFileWithPresignedUrl(report.analysis_file_url, 'AI Analysis'); }}
                              className="flex items-center gap-1.5 px-3 py-2 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg text-sm font-medium transition-colors cursor-pointer"
                              title="View AI Analysis - Abnormal & Borderline Values"
                            >
                              <FileText className="w-4 h-4 text-purple-600" />
                              <span className="text-purple-700">AI Analysis</span>
                              {(report.abnormal_count > 0 || report.borderline_count > 0) && (
                                <span className="flex gap-1.5">
                                  {report.abnormal_count > 0 && (
                                    <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded font-bold">
                                      {report.abnormal_count} Abnormal
                                    </span>
                                  )}
                                  {report.borderline_count > 0 && (
                                    <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded font-bold">
                                      {report.borderline_count} Borderline
                                    </span>
                                  )}
                                </span>
                              )}
                            </button>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                setReanalyzingReports(prev => ({...prev, [report.id]: true}));
                                try {
                                  await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/report-uploads/${report.id}/reanalyze`);
                                  const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/report-uploads/session/${viewSession.id}`);
                                  const reportsMap = {...sessionReports};
                                  reportsMap[viewSession.id] = response.data;
                                  setSessionReports(reportsMap);
                                } catch (error) {
                                  console.error('Re-analyze error:', error);
                                } finally {
                                  setReanalyzingReports(prev => ({...prev, [report.id]: false}));
                                }
                              }}
                              disabled={reanalyzingReports[report.id]}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors disabled:opacity-50"
                              title="Re-analyze report"
                            >
                              <svg className={`w-3 h-3 ${reanalyzingReports[report.id] ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              <span>{reanalyzingReports[report.id] ? 'Analyzing...' : 'Re-analyze'}</span>
                            </button>
                          </div>
                        )}
                        {/* Original report file */}
                        <button
                          onClick={(e) => { e.stopPropagation(); openFileWithPresignedUrl(report.report_url, report.test_name || 'Report'); }}
                          className="flex items-center space-x-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors max-w-xs cursor-pointer"
                          title={`${report.test_name} - ${report.notes || 'Report'}`}
                        >
                          <FileText className="w-4 h-4 text-blue-600" />
                          <div className="flex flex-col">
                            <span className="text-sm text-blue-900 font-medium">{report.test_name}</span>
                            {report.notes && <span className="text-xs text-blue-700 truncate">{report.notes}</span>}
                          </div>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowViewDialog(false)}>
                  Close
                </Button>
                <Button 
                  onClick={() => {
                    handlePrintPreview(viewSession);
                    setShowViewDialog(false);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Prescription
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
